import { useState, useRef, useCallback } from 'react';
import { Upload, X, Image } from 'lucide-react';

interface LogoDropzoneProps {
  value: string;
  onChange: (dataUrl: string) => void;
}

const MAX_SIZE = 2 * 1024 * 1024;
const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/jpg'];

function resizeImage(file: File, maxDim: number): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new window.Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > maxDim || height > maxDim) {
          const ratio = Math.min(maxDim / width, maxDim / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) { reject(new Error('Canvas not supported')); return; }
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL(file.type, 0.85));
      };
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = reader.result as string;
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

export default function LogoDropzone({ value, onChange }: LogoDropzoneProps) {
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const processFile = useCallback(async (file: File) => {
    setError('');
    if (!ACCEPTED_TYPES.includes(file.type)) {
      setError('Only PNG and JPEG files are supported');
      return;
    }
    if (file.size > MAX_SIZE) {
      setError('File must be under 2MB');
      return;
    }
    try {
      const dataUrl = await resizeImage(file, 512);
      onChange(dataUrl);
    } catch {
      setError('Failed to process image');
    }
  }, [onChange]);

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) processFile(file);
  }

  function handleDragOver(e: React.DragEvent) {
    e.preventDefault();
    setDragging(true);
  }

  function handleDragLeave(e: React.DragEvent) {
    e.preventDefault();
    setDragging(false);
  }

  function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) processFile(file);
    if (inputRef.current) inputRef.current.value = '';
  }

  if (value) {
    return (
      <div className="flex items-center gap-3 p-3 glass-inner rounded-xl">
        <img src={value} alt="Logo preview" className="w-14 h-14 rounded-lg object-contain bg-white" />
        <div className="flex-1 min-w-0">
          <p className="text-xs text-white/60 truncate">Logo uploaded</p>
        </div>
        <button
          type="button"
          onClick={() => onChange('')}
          className="p-1.5 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors"
        >
          <X size={16} />
        </button>
      </div>
    );
  }

  return (
    <div>
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => inputRef.current?.click()}
        className={`relative cursor-pointer rounded-xl border-2 border-dashed transition-all duration-200 p-6 flex flex-col items-center gap-2 ${
          dragging
            ? 'border-teal-400 bg-teal-500/10'
            : 'border-white/15 hover:border-white/30 bg-white/5 hover:bg-white/8'
        }`}
      >
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors ${
          dragging ? 'bg-teal-500/20' : 'bg-white/10'
        }`}>
          {dragging ? <Image size={20} className="text-teal-400" /> : <Upload size={20} className="text-white/50" />}
        </div>
        <p className="text-xs text-white/60 text-center">
          {dragging ? 'Drop to upload' : 'Drag & drop logo here, or tap to browse'}
        </p>
        <p className="text-[10px] text-white/40">PNG or JPEG, max 2MB</p>
        <input
          ref={inputRef}
          type="file"
          accept=".png,.jpg,.jpeg,image/png,image/jpeg"
          onChange={handleFileSelect}
          className="hidden"
        />
      </div>
      {error && <p className="text-xs text-red-400 mt-1.5">{error}</p>}
    </div>
  );
}
